import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Save, ExternalLink, Loader2 } from 'lucide-react';
import { checkAuth, clearToken, saveContent } from '../../admin/api';
import defaultContent from '../../content/defaultContent';
import { deepMerge } from '../../content/ContentContext';
import { Text, Area, Color, IconSelect, ImageInput, ListEditor } from './fields';
import AdminTabs from './AdminTabs';
import BrandMark from '../../components/BrandMark';

const TABS = [
  { key: 'site', label: '基本信息' },
  { key: 'slides', label: '首页轮播' },
  { key: 'homeSections', label: '首页板块' },
  { key: 'courses', label: '课程' },
  { key: 'about', label: '关于我们' },
  { key: 'schools', label: '学校合作' },
  { key: 'contactPage', label: '联系页' },
];

export default function Dashboard() {
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState('site');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    (async () => {
      const ok = await checkAuth();
      if (!ok) { nav('/admin/login'); return; }
      try {
        const r = await fetch('/api/content', { cache: 'no-store' });
        const data = r.ok ? await r.json() : {};
        setC(deepMerge(defaultContent, data || {}));
      } catch {
        setC(deepMerge(defaultContent, {}));
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2500); };

  // 按路径更新嵌套内容：set(['home','services','title'], v)
  const set = (path, value) => {
    setC((prev) => {
      const next = structuredClone(prev);
      let node = next;
      for (let i = 0; i < path.length - 1; i++) node = node[path[i]];
      node[path[path.length - 1]] = value;
      return next;
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await saveContent(c);
      flash('已保存，刷新官网即可看到更新');
    } catch (e) {
      flash(e.message);
    } finally {
      setSaving(false);
    }
  };

  const logout = () => { clearToken(); nav('/admin/login'); };

  if (loading || !c) {
    return (
      <div className="flex min-h-screen items-center justify-center text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> 加载中…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <BrandMark size="sm" />
            <div className="font-bold text-slate-900 max-sm:hidden">{c.site.brandName} · 管理后台</div>
          </div>
          <AdminTabs />
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 max-sm:hidden">
              <ExternalLink className="h-4 w-4" /> 看官网
            </a>
            <button onClick={logout} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 cursor-pointer">
              <LogOut className="h-4 w-4" /> 退出
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="mx-auto max-w-5xl overflow-x-auto px-4">
          <div className="flex gap-1 pb-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  tab === t.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        {tab === 'site' && <SiteTab c={c} set={set} />}
        {tab === 'slides' && <SlidesTab c={c} set={set} />}
        {tab === 'homeSections' && <HomeSectionsTab c={c} set={set} />}
        {tab === 'courses' && <CoursesTab c={c} set={set} />}
        {tab === 'about' && <AboutTab c={c} set={set} />}
        {tab === 'schools' && <SchoolsTab c={c} set={set} />}
        {tab === 'contactPage' && <ContactTab c={c} set={set} />}
      </main>

      <div className="sticky bottom-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <span className="text-sm text-gray-500">修改后记得保存，全站内容统一存于后端</span>
          <button onClick={onSave} disabled={saving} className="btn btn-blue inline-flex items-center gap-1.5 py-2.5 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? '保存中…' : '保存全部'}
          </button>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg">{toast}</div>
      )}
    </div>
  );
}

// ---------- 区块外壳 ----------
function Card({ title, children }) {
  return (
    <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-soft">
      {title && <h2 className="mb-4 text-lg font-bold text-slate-900">{title}</h2>}
      <div className="space-y-4">{children}</div>
    </section>
  );
}

// ---------- 各 Tab ----------
function SiteTab({ c, set }) {
  const s = c.site;
  return (
    <Card title="网站基本信息">
      <div className="grid gap-4 sm:grid-cols-2">
        <Text label="品牌名称" value={s.brandName} onChange={(v) => set(['site', 'brandName'], v)} />
        <Text label="英文名" value={s.brandEn} onChange={(v) => set(['site', 'brandEn'], v)} />
        <Text label="产品系列名" value={s.productBrand} onChange={(v) => set(['site', 'productBrand'], v)} />
        <Text label="公司主体" value={s.company} onChange={(v) => set(['site', 'company'], v)} />
        <Text label="电话" value={s.phone} onChange={(v) => set(['site', 'phone'], v)} />
        <Text label="邮箱" value={s.email} onChange={(v) => set(['site', 'email'], v)} />
        <Text label="地址" value={s.address} onChange={(v) => set(['site', 'address'], v)} />
        <Text label="服务时间" value={s.hours} onChange={(v) => set(['site', 'hours'], v)} />
        <Text label="ICP 备案号" value={s.icp} onChange={(v) => set(['site', 'icp'], v)} />
        <Text label="公网安备号" value={s.police} onChange={(v) => set(['site', 'police'], v)} />
      </div>
      <Area label="页脚说明" value={s.footerNote} onChange={(v) => set(['site', 'footerNote'], v)} rows={2} />
    </Card>
  );
}

function SlidesTab({ c, set }) {
  const slides = c.home.slides;
  return (
    <Card title="首页轮播 Banner">
      <ListEditor
        items={slides}
        onChange={(v) => set(['home', 'slides'], v)}
        newItem={() => ({ id: Date.now(), image: '', gradient: ['#1e3a8a', '#2563eb'], eyebrow: '', title: '', subtitle: '', ctaText: '', ctaLink: '', altText: '', altLink: '' })}
        addLabel="新增一张幻灯片"
        render={(it, up) => (
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <ImageInput label="背景图片" value={it.image} onChange={(v) => up({ image: v })} fallback={it.gradient} />
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Color label="兜底渐变 1" value={it.gradient?.[0]} onChange={(v) => up({ gradient: [v, it.gradient?.[1] || '#2563eb'] })} />
                <Color label="兜底渐变 2" value={it.gradient?.[1]} onChange={(v) => up({ gradient: [it.gradient?.[0] || '#1e3a8a', v] })} />
              </div>
            </div>
            <div className="space-y-3">
              <Text label="点击跳转链接（如 /news/science-museum-course）" value={it.link} onChange={(v) => up({ link: v })} />
              <p className="text-xs text-gray-400">轮播只展示图片，点击整张图会跳到此链接（一般填新闻详情页）。</p>
              <Text label="图片说明（无障碍 alt，可选）" value={it.eyebrow} onChange={(v) => up({ eyebrow: v })} />
            </div>
          </div>
        )}
      />
    </Card>
  );
}

function HomeSectionsTab({ c, set }) {
  const h = c.home;
  return (
    <>
      <Card title="产品/服务介绍">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="小标签" value={h.services.eyebrow} onChange={(v) => set(['home', 'services', 'eyebrow'], v)} />
          <Text label="标题" value={h.services.title} onChange={(v) => set(['home', 'services', 'title'], v)} />
        </div>
        <Area label="描述" value={h.services.desc} onChange={(v) => set(['home', 'services', 'desc'], v)} rows={2} />
        <ListEditor
          items={h.services.items}
          onChange={(v) => set(['home', 'services', 'items'], v)}
          newItem={() => ({ icon: 'Cpu', title: '', desc: '', link: '' })}
          addLabel="新增一个服务"
          render={(it, up) => (
            <div className="grid gap-3 sm:grid-cols-2">
              <IconSelect label="图标" value={it.icon} onChange={(v) => up({ icon: v })} />
              <Text label="链接（选填）" value={it.link} onChange={(v) => up({ link: v })} />
              <Text label="标题" value={it.title} onChange={(v) => up({ title: v })} />
              <Area label="描述" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
            </div>
          )}
        />
      </Card>

      <Card title="课堂现场图片区">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="小标签" value={h.proof?.eyebrow} onChange={(v) => set(['home', 'proof', 'eyebrow'], v)} />
          <Text label="标题" value={h.proof?.title} onChange={(v) => set(['home', 'proof', 'title'], v)} />
        </div>
        <Area label="描述" value={h.proof?.desc} onChange={(v) => set(['home', 'proof', 'desc'], v)} rows={2} />
        <ListEditor
          items={h.proof?.items || []}
          onChange={(v) => set(['home', 'proof', 'items'], v)}
          newItem={() => ({ src: '', title: '', desc: '' })}
          addLabel="新增一张展示图"
          render={(it, up) => (
            <div className="grid gap-3 md:grid-cols-2">
              <ImageInput label="图片" value={it.src} onChange={(v) => up({ src: v })} />
              <div className="space-y-3">
                <Text label="标题" value={it.title} onChange={(v) => up({ title: v })} />
                <Area label="描述" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
              </div>
            </div>
          )}
        />
      </Card>

      <Card title="科研支持模块">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="小标签" value={h.support?.eyebrow} onChange={(v) => set(['home', 'support', 'eyebrow'], v)} />
          <Text label="标题" value={h.support?.title} onChange={(v) => set(['home', 'support', 'title'], v)} />
        </div>
        <ImageInput label="配图" value={h.support?.image} onChange={(v) => set(['home', 'support', 'image'], v)} />
        <Text label="图片说明 alt" value={h.support?.alt} onChange={(v) => set(['home', 'support', 'alt'], v)} />
        <Area label="描述" value={h.support?.desc} onChange={(v) => set(['home', 'support', 'desc'], v)} rows={3} />
      </Card>

      <Card title="核心优势">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="小标签" value={h.advantages.eyebrow} onChange={(v) => set(['home', 'advantages', 'eyebrow'], v)} />
          <Text label="标题" value={h.advantages.title} onChange={(v) => set(['home', 'advantages', 'title'], v)} />
        </div>
        <ListEditor
          items={h.advantages.items}
          onChange={(v) => set(['home', 'advantages', 'items'], v)}
          newItem={() => ({ icon: 'Star', title: '', desc: '' })}
          addLabel="新增一条优势"
          render={(it, up) => (
            <div className="grid gap-3 sm:grid-cols-2">
              <IconSelect label="图标" value={it.icon} onChange={(v) => up({ icon: v })} />
              <Text label="标题" value={it.title} onChange={(v) => up({ title: v })} />
              <Area label="描述" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
            </div>
          )}
        />
      </Card>

      <Card title="案例与成果">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="小标签" value={h.results.eyebrow} onChange={(v) => set(['home', 'results', 'eyebrow'], v)} />
          <Text label="标题" value={h.results.title} onChange={(v) => set(['home', 'results', 'title'], v)} />
        </div>
        <ListEditor
          items={h.results.stats}
          onChange={(v) => set(['home', 'results', 'stats'], v)}
          newItem={() => ({ n: '', l: '' })}
          addLabel="新增一个数据"
          render={(it, up) => (
            <div className="grid grid-cols-2 gap-3">
              <Text label="数字" value={it.n} onChange={(v) => up({ n: v })} />
              <Text label="说明" value={it.l} onChange={(v) => up({ l: v })} />
            </div>
          )}
        />
        <Area label="案例引言" value={h.results.quote} onChange={(v) => set(['home', 'results', 'quote'], v)} rows={2} />
        <div className="grid grid-cols-2 gap-3">
          <Text label="引言人姓名" value={h.results.quoteName} onChange={(v) => set(['home', 'results', 'quoteName'], v)} />
          <Text label="引言人身份" value={h.results.quoteRole} onChange={(v) => set(['home', 'results', 'quoteRole'], v)} />
        </div>
      </Card>

      <Card title="联系区文案">
        <Text label="小标签" value={h.contact.eyebrow} onChange={(v) => set(['home', 'contact', 'eyebrow'], v)} />
        <Text label="标题" value={h.contact.title} onChange={(v) => set(['home', 'contact', 'title'], v)} />
        <Area label="描述" value={h.contact.desc} onChange={(v) => set(['home', 'contact', 'desc'], v)} rows={2} />
      </Card>

      <Card title="科研背景背书">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="小标签" value={h.research?.eyebrow} onChange={(v) => set(['home', 'research', 'eyebrow'], v)} />
          <Text label="标题" value={h.research?.title} onChange={(v) => set(['home', 'research', 'title'], v)} />
        </div>
        <Area label="描述" value={h.research?.desc} onChange={(v) => set(['home', 'research', 'desc'], v)} rows={3} />
        <Area label="研究方向标签（每行一个）" value={(h.research?.directions || []).join('\n')} onChange={(v) => set(['home', 'research', 'directions'], v.split('\n').map((x) => x.trim()).filter(Boolean))} rows={3} />
        <ListEditor
          items={h.research?.points || []}
          onChange={(v) => set(['home', 'research', 'points'], v)}
          newItem={() => ({ icon: 'FlaskConical', title: '', desc: '' })}
          addLabel="新增一个要点"
          render={(it, up) => (
            <div className="grid gap-3 sm:grid-cols-2">
              <IconSelect label="图标" value={it.icon} onChange={(v) => up({ icon: v })} />
              <Text label="标题" value={it.title} onChange={(v) => up({ title: v })} />
              <Area label="描述" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
            </div>
          )}
        />
      </Card>

      <Card title="发展历程时间轴">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="小标签" value={h.timeline?.eyebrow} onChange={(v) => set(['home', 'timeline', 'eyebrow'], v)} />
          <Text label="标题" value={h.timeline?.title} onChange={(v) => set(['home', 'timeline', 'title'], v)} />
        </div>
        <Area label="描述" value={h.timeline?.desc} onChange={(v) => set(['home', 'timeline', 'desc'], v)} rows={2} />
        <ListEditor
          items={h.timeline?.items || []}
          onChange={(v) => set(['home', 'timeline', 'items'], v)}
          newItem={() => ({ year: '起点', title: '', desc: '' })}
          addLabel="新增一个阶段"
          render={(it, up) => (
            <div className="space-y-3">
              <Text label="年份/阶段（如 2023 年或起点）" value={it.year} onChange={(v) => up({ year: v })} />
              <Text label="标题" value={it.title} onChange={(v) => up({ title: v })} />
              <Area label="描述" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
            </div>
          )}
        />
      </Card>
    </>
  );
}

function CoursesTab({ c, set }) {
  const co = c.courses;
  return (
    <>
      <Card title="课程页头部">
        <Text label="小标签" value={co.hero.eyebrow} onChange={(v) => set(['courses', 'hero', 'eyebrow'], v)} />
        <Text label="标题" value={co.hero.title} onChange={(v) => set(['courses', 'hero', 'title'], v)} />
        <Area label="描述" value={co.hero.desc} onChange={(v) => set(['courses', 'hero', 'desc'], v)} rows={2} />
      </Card>
      <Card title="课程列表">
        <ListEditor
          items={co.items}
          onChange={(v) => set(['courses', 'items'], v)}
          newItem={() => ({ id: 'c' + Date.now(), icon: 'BookOpen', image: '', name: '', level: '', age: '', color: '#2563eb', soft: '#eff6ff', tagline: '', detail: '', badge: '', learn: [], stages: { primary: '', middle: '', high: '' } })}
          addLabel="新增一门课程"
          render={(it, up) => (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-3">
                <ImageInput label="课程配图（选填，留空显示图标）" value={it.image} onChange={(v) => up({ image: v })} fallback={[it.color, it.color]} />
                <IconSelect label="图标" value={it.icon} onChange={(v) => up({ icon: v })} />
                <div className="grid grid-cols-2 gap-2">
                  <Color label="主色" value={it.color} onChange={(v) => up({ color: v })} />
                  <Color label="浅底色" value={it.soft} onChange={(v) => up({ soft: v })} />
                </div>
              </div>
              <div className="space-y-3">
                <Text label="课程名" value={it.name} onChange={(v) => up({ name: v })} />
                <div className="grid grid-cols-3 gap-2">
                  <Text label="难度" value={it.level} onChange={(v) => up({ level: v })} />
                  <Text label="年龄" value={it.age} onChange={(v) => up({ age: v })} />
                  <Text label="角标" value={it.badge} onChange={(v) => up({ badge: v })} />
                </div>
                <Text label="一句话亮点" value={it.tagline} onChange={(v) => up({ tagline: v })} />
                <Area label="详细介绍" value={it.detail} onChange={(v) => up({ detail: v })} rows={3} />
                <Area label="能学到（每行一条）" value={(it.learn || []).join('\n')} onChange={(v) => up({ learn: v.split('\n').map((x) => x.trim()).filter(Boolean) })} rows={3} />
                <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
                  <div className="text-xs font-semibold text-gray-500">各学段涉及</div>
                  <Text label="小学" value={it.stages?.primary} onChange={(v) => up({ stages: { ...it.stages, primary: v } })} />
                  <Text label="中学" value={it.stages?.middle} onChange={(v) => up({ stages: { ...it.stages, middle: v } })} />
                  <Text label="高中" value={it.stages?.high} onChange={(v) => up({ stages: { ...it.stages, high: v } })} />
                </div>
              </div>
            </div>
          )}
        />
      </Card>
    </>
  );
}

function AboutTab({ c, set }) {
  const a = c.about;
  const h = c.home;
  return (
    <>
      <Card title="关于页头部">
        <Text label="小标签" value={a.hero.eyebrow} onChange={(v) => set(['about', 'hero', 'eyebrow'], v)} />
        <Text label="标题" value={a.hero.title} onChange={(v) => set(['about', 'hero', 'title'], v)} />
      </Card>
      <Card title="品牌故事（每段一行）">
        <Area value={(a.story || []).join('\n')} onChange={(v) => set(['about', 'story'], v.split('\n').map((x) => x.trim()).filter(Boolean))} rows={6} />
      </Card>
      {/* aboutSupportMirror */}
      <Card title="课堂现场图片区">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="小标签" value={h.proof?.eyebrow} onChange={(v) => set(['home', 'proof', 'eyebrow'], v)} />
          <Text label="标题" value={h.proof?.title} onChange={(v) => set(['home', 'proof', 'title'], v)} />
        </div>
        <Area label="描述" value={h.proof?.desc} onChange={(v) => set(['home', 'proof', 'desc'], v)} rows={2} />
        <ListEditor
          items={h.proof?.items || []}
          onChange={(v) => set(['home', 'proof', 'items'], v)}
          newItem={() => ({ src: '', title: '', desc: '' })}
          addLabel="新增一张展示图"
          render={(it, up) => (
            <div className="grid gap-3 md:grid-cols-2">
              <ImageInput label="图片" value={it.src} onChange={(v) => up({ src: v })} />
              <div className="space-y-3">
                <Text label="标题" value={it.title} onChange={(v) => up({ title: v })} />
                <Area label="描述" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
              </div>
            </div>
          )}
        />
      </Card>

      <Card title="科研支持模块">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="小标签" value={h.support?.eyebrow} onChange={(v) => set(['home', 'support', 'eyebrow'], v)} />
          <Text label="标题" value={h.support?.title} onChange={(v) => set(['home', 'support', 'title'], v)} />
        </div>
        <ImageInput label="配图" value={h.support?.image} onChange={(v) => set(['home', 'support', 'image'], v)} />
        <Text label="图片说明 alt" value={h.support?.alt} onChange={(v) => set(['home', 'support', 'alt'], v)} />
        <Area label="描述" value={h.support?.desc} onChange={(v) => set(['home', 'support', 'desc'], v)} rows={3} />
      </Card>

      <Card title="我们相信什么">
        <Text label="板块标题" value={a.values.title} onChange={(v) => set(['about', 'values', 'title'], v)} />
        <ListEditor
          items={a.values.items}
          onChange={(v) => set(['about', 'values', 'items'], v)}
          newItem={() => ({ icon: 'Heart', title: '', desc: '' })}
          addLabel="新增一条"
          render={(it, up) => (
            <div className="grid gap-3 sm:grid-cols-2">
              <IconSelect label="图标" value={it.icon} onChange={(v) => up({ icon: v })} />
              <Text label="标题" value={it.title} onChange={(v) => up({ title: v })} />
              <Area label="描述" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
            </div>
          )}
        />
      </Card>
      <Card title="团队">
        <div className="grid gap-3 sm:grid-cols-2">
          <Text label="板块标题" value={a.team.title} onChange={(v) => set(['about', 'team', 'title'], v)} />
          <Text label="副标题" value={a.team.subtitle} onChange={(v) => set(['about', 'team', 'subtitle'], v)} />
        </div>
        <ListEditor
          items={a.team.members}
          onChange={(v) => set(['about', 'team', 'members'], v)}
          newItem={() => ({ name: '', role: '', desc: '', color: '#2563eb', image: '' })}
          addLabel="新增一位成员"
          render={(it, up) => (
            <div className="grid gap-3 md:grid-cols-2">
              <ImageInput label="头像（选填，留空显示首字）" value={it.image} onChange={(v) => up({ image: v })} fallback={[it.color, it.color]} />
              <div className="space-y-3">
                <Text label="姓名/花名" value={it.name} onChange={(v) => up({ name: v })} />
                <Text label="职责" value={it.role} onChange={(v) => up({ role: v })} />
                <Color label="主题色" value={it.color} onChange={(v) => up({ color: v })} />
                <Area label="简介" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
              </div>
            </div>
          )}
        />
      </Card>
    </>
  );
}

function SchoolsTab({ c, set }) {
  const s = c.schools;
  return (
    <>
      <Card title="学校页头部">
        <Text label="小标签" value={s.hero.eyebrow} onChange={(v) => set(['schools', 'hero', 'eyebrow'], v)} />
        <Text label="标题" value={s.hero.title} onChange={(v) => set(['schools', 'hero', 'title'], v)} />
        <Area label="描述" value={s.hero.desc} onChange={(v) => set(['schools', 'hero', 'desc'], v)} rows={2} />
      </Card>
      <Card title="我们提供什么">
        <Text label="板块标题" value={s.offers.title} onChange={(v) => set(['schools', 'offers', 'title'], v)} />
        <ListEditor
          items={s.offers.items}
          onChange={(v) => set(['schools', 'offers', 'items'], v)}
          newItem={() => ({ icon: 'Package', title: '', desc: '' })}
          addLabel="新增一项"
          render={(it, up) => (
            <div className="grid gap-3 sm:grid-cols-2">
              <IconSelect label="图标" value={it.icon} onChange={(v) => up({ icon: v })} />
              <Text label="标题" value={it.title} onChange={(v) => up({ title: v })} />
              <Area label="描述" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
            </div>
          )}
        />
      </Card>
      <Card title="合作流程">
        <Text label="板块标题" value={s.steps.title} onChange={(v) => set(['schools', 'steps', 'title'], v)} />
        <ListEditor
          items={s.steps.items}
          onChange={(v) => set(['schools', 'steps', 'items'], v)}
          newItem={() => ({ title: '', desc: '' })}
          addLabel="新增一步"
          render={(it, up) => (
            <div className="grid gap-3 sm:grid-cols-2">
              <Text label="步骤标题" value={it.title} onChange={(v) => up({ title: v })} />
              <Area label="说明" value={it.desc} onChange={(v) => up({ desc: v })} rows={2} />
            </div>
          )}
        />
      </Card>
    </>
  );
}

function ContactTab({ c, set }) {
  const p = c.contactPage;
  return (
    <>
      <Card title="联系页头部">
        <Text label="小标签" value={p.hero.eyebrow} onChange={(v) => set(['contactPage', 'hero', 'eyebrow'], v)} />
        <Text label="标题" value={p.hero.title} onChange={(v) => set(['contactPage', 'hero', 'title'], v)} />
        <Area label="描述" value={p.hero.desc} onChange={(v) => set(['contactPage', 'hero', 'desc'], v)} rows={2} />
        <p className="text-xs text-gray-400">提示：电话/邮箱/地址等联系方式在「基本信息」里统一修改。</p>
      </Card>
      <Card title="常见问题">
        <ListEditor
          items={p.faqs}
          onChange={(v) => set(['contactPage', 'faqs'], v)}
          newItem={() => ({ q: '', a: '' })}
          addLabel="新增一个问题"
          render={(it, up) => (
            <div className="space-y-3">
              <Text label="问题" value={it.q} onChange={(v) => up({ q: v })} />
              <Area label="回答" value={it.a} onChange={(v) => up({ a: v })} rows={2} />
            </div>
          )}
        />
      </Card>
    </>
  );
}